// Conversion tracking + remarketing — single source of truth.
// All IDs are read from REACT_APP_* env vars (set in Emergent Secrets at deploy time).
// If an ID is empty, that pixel is silently skipped — production safe.
//
// HOW TO ENABLE GOOGLE ADS CONVERSIONS:
//   1. In Google Ads → Tools → Conversions, create the conversion actions you want to track
//      (e.g. "FCA Website Lead").
//   2. Each conversion gives you two values:
//        • Conversion ID    — looks like "AW-123456789"
//        • Conversion Label — looks like "abcDEFghIJK_lmnOPQ"
//   3. Set these env vars in /app/frontend/.env (or your Emergent Secrets):
//        REACT_APP_GOOGLE_ADS_ID=AW-123456789
//        REACT_APP_GOOGLE_ADS_CONVERSION_LABEL=abcDEFghIJK_lmnOPQ
//   4. Redeploy. gtag.js + dataLayer continue to work for GA4 in parallel.
//
// PRIVACY (Task 7): Enhanced Conversions have been REMOVED. No lead identity
// (name / email / phone) is ever collected for or transmitted to Google. The
// single Google Ads "FCA Website Lead" primary conversion still fires on
// genuine, server-confirmed enquiry/booking submissions — using only a
// non-identifying transaction_id (the backend submission id) for de-duplication.

export const TRACKING = {
  googleAdsId: process.env.REACT_APP_GOOGLE_ADS_ID || "",                       // e.g. "AW-1234567890"
  googleAdsConversionLabel: process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || "", // e.g. "abcdEFG1234"
  ga4Id: process.env.REACT_APP_GA4_ID || "",                                    // e.g. "G-XXXXXXXXXX"
  metaPixelId: process.env.REACT_APP_META_PIXEL_ID || "",                       // e.g. "1234567890"
  linkedInPartnerId: process.env.REACT_APP_LINKEDIN_PARTNER_ID || "",           // e.g. "1234567"
  gtmContainerId: process.env.REACT_APP_GTM_CONTAINER_ID || "",                 // e.g. "GTM-XXXXXXX" (optional)
};

export const hasGoogleAds = () => !!TRACKING.googleAdsId;
export const hasGoogleAdsConversion = () => !!(TRACKING.googleAdsId && TRACKING.googleAdsConversionLabel);
export const hasGA4 = () => !!TRACKING.ga4Id;
// gtag.js loader is shared between Google Ads and GA4 — we only need to inject it once
export const hasGtagLoader = () => hasGoogleAds() || hasGA4();
export const hasMetaPixel = () => !!TRACKING.metaPixelId;
export const hasLinkedIn = () => !!TRACKING.linkedInPartnerId;
export const hasGTM = () => !!TRACKING.gtmContainerId;

// ─── Test isolation (preview-only) ──────────────────────────────────────────
// The sandbox requires BOTH an explicit flag AND a preview host. On the
// production domain it can NEVER activate — even if the flag is accidentally
// built in — because _isPreviewHost() is false there. When sandboxed, NO real
// gtag/fbq/lintrk calls are made; every event is recorded into
// window.__fca_tracking_log so automated tests can assert "exactly one lead
// event, no duplicates, no PII" WITHOUT firing to the live Google Ads / GA4
// property. Production fires normally.
function _isPreviewHost() {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname || "";
  return h.endsWith(".preview.emergentagent.com") || h === "localhost" || h === "127.0.0.1";
}
export const TRACKING_SANDBOX =
  (process.env.REACT_APP_TRACKING_SANDBOX === "true") && _isPreviewHost();

function recordTracking(entry) {
  if (typeof window === "undefined") return;
  window.__fca_tracking_log = window.__fca_tracking_log || [];
  window.__fca_tracking_log.push({ ...entry, ts: Date.now() });
}

// True only when we may call the live gtag. In sandbox mode this is always
// false, so no event ever reaches the real analytics/ads property.
function gtagReady() {
  return !TRACKING_SANDBOX && typeof window !== "undefined" && typeof window.gtag === "function";
}

// Push to GTM dataLayer if present (always safe — local array, auto-created if missing)
function dataLayerPush(event) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

/**
 * Fire a booking-completed conversion.
 * Returns the event payload (useful for testing/debugging).
 *
 * Only a non-identifying bookingId (backend submission id) is sent to Google as
 * transaction_id for de-duplication. No lead identity is transmitted.
 */
export function trackBookingConversion({ value = 2910, currency = "AUD", bookingId = "", sourcePage = "" } = {}) {
  const eventPayload = {
    event: "fca_booking_completed",
    booking_id: bookingId,
    source_page: sourcePage,
    value,
    currency,
  };

  // GTM dataLayer (universal — works whether or not GTM is loaded)
  dataLayerPush(eventPayload);
  recordTracking({ kind: "lead", event: "fca_booking_completed", source_page: sourcePage, transaction_id: bookingId, value, currency });

  if (!gtagReady()) return eventPayload;

  // Google Ads primary conversion — FCA Website Lead (booking is a genuine enquiry)
  if (hasGoogleAdsConversion()) {
    window.gtag("event", "conversion", {
      send_to: `${TRACKING.googleAdsId}/${TRACKING.googleAdsConversionLabel}`,
      value,
      currency,
      transaction_id: bookingId,
    });
  }

  // Meta Pixel — Lead event (standard) + custom event for granularity
  if (hasMetaPixel() && typeof window.fbq === "function") {
    window.fbq("track", "Lead", { value, currency, content_name: "FCA Booking" });
    window.fbq("trackCustom", "FCABookingCompleted", { value, currency, source_page: sourcePage });
  }

  // LinkedIn — Conversion event (uses standard tag-firing via lintrk if present)
  if (hasLinkedIn() && typeof window.lintrk === "function") {
    window.lintrk("track", { conversion_id: TRACKING.linkedInPartnerId });
  }

  return eventPayload;
}

/**
 * Fire a booking-initiation event (when the user picks day + time and reaches step 3).
 * Used for remarketing audiences of warm visitors who didn't complete.
 */
export function trackBookingInitiated({ sourcePage = "" } = {}) {
  const eventPayload = {
    event: "fca_booking_initiated",
    source_page: sourcePage,
  };
  dataLayerPush(eventPayload);
  recordTracking({ kind: "micro", event: "fca_booking_initiated", source_page: sourcePage });

  if (!gtagReady()) return eventPayload;

  if (hasGoogleAds()) {
    window.gtag("event", "begin_checkout", { send_to: TRACKING.googleAdsId, items: [{ item_name: "FCA Booking" }] });
  }
  if (hasMetaPixel() && typeof window.fbq === "function") {
    window.fbq("track", "InitiateCheckout", { content_name: "FCA Booking" });
  }
  return eventPayload;
}

/**
 * Fire a page-view event (for remarketing audiences). PixelScripts.jsx initialises page-load
 * pixel-side; this function is for SPA route changes so single-page-app navigation is captured.
 */
export function trackPageView({ path = "" } = {}) {
  dataLayerPush({ event: "page_view", page_path: path });
  if (!gtagReady()) return;

  // GA4 — fire page_view (recommended approach for SPAs)
  if (hasGA4()) {
    window.gtag("event", "page_view", {
      send_to: TRACKING.ga4Id,
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
  if (hasGoogleAds()) {
    window.gtag("event", "page_view", { send_to: TRACKING.googleAdsId, page_path: path });
  }
  if (hasMetaPixel() && typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
}

/**
 * Fire a GA4 custom event + push to the dataLayer for a MICRO-engagement.
 *
 * IMPORTANT — this helper deliberately does NOT fire the Google Ads
 * `FCA Website Lead` primary conversion. Micro-events (phone/email clicks,
 * Book Appointment intent, Sample Report / lead-magnet downloads, coordinator
 * pack downloads, `data-conversion` CTAs, etc.) are analytics-only signals —
 * they must not be counted against the primary conversion action because
 * they inflate the count and mistrain Smart Bidding.
 *
 * Genuine lead submissions (ContactForm → /thank-you, /lp/* AdsLandingPage,
 * Pricing enquiry, Refer a Participant) use `trackFormSubmit()` below, and
 * genuine BookingWidget completions use `trackBookingConversion()`. Both of
 * those DO fire the Google Ads primary conversion.
 *
 *   eventName    — short snake_case label, e.g. "phone_click", "email_click"
 *   params       — optional { value, currency, transaction_id, source_page, ...extra }
 */
export function trackConversion(eventName, params = {}) {
  const { value, currency = "AUD", transaction_id, ...extra } = params;

  // Always push to dataLayer (works whether GTM is loaded or not — also captured by PostHog).
  dataLayerPush({ event: eventName, ...params });
  recordTracking({ kind: "micro", event: eventName, source_page: params.source_page || "", transaction_id: transaction_id || "" });

  if (!gtagReady()) return;

  // GA4 — custom event (fires to the GA4 property automatically if it's configured).
  if (hasGA4()) {
    window.gtag("event", eventName, {
      send_to: TRACKING.ga4Id,
      ...(value !== undefined ? { value, currency } : {}),
      ...(transaction_id ? { transaction_id } : {}),
      ...extra,
    });
  }

  // Deliberately no `gtag('event','conversion', { send_to: AW-...})` here.
  // The Google Ads FCA Website Lead conversion is fired ONLY by
  // trackFormSubmit() (genuine enquiry/referral) and trackBookingConversion()
  // (genuine booking) below.
}

// Convenience wrappers — call from click handlers / page-mount effects
//
// trackFormSubmit() — fire on SUCCESSFUL enquiry-form / lead submission ONLY.
//   - Pushes `form_submit` (custom event) to dataLayer for GTM
//   - Fires GA4 `generate_lead` (the GA4-recommended lead event name)
//   - Fires the Google Ads `conversion` event (the single FCA Website Lead
//     primary WEBSITE-ENQUIRY action) with only a non-identifying
//     transaction_id for de-duplication. NO personal data is ever attached.
//   - DO NOT call this on page load. Only on confirmed form submission.
export function trackFormSubmit(params = {}) {
  const { value, currency = "AUD", transaction_id, source_page = "", conversion_type = "enquiry", ...extra } = params;

  // GTM dataLayer — universal, works whether GTM is loaded or not
  dataLayerPush({
    event: "form_submit",
    source_page,
    conversion_type,
    ...(value !== undefined ? { value, currency } : {}),
    ...(transaction_id ? { transaction_id } : {}),
    ...extra,
  });
  dataLayerPush({
    event: "generate_lead",
    source_page,
    conversion_type,
    ...(value !== undefined ? { value, currency } : {}),
    ...(transaction_id ? { transaction_id } : {}),
  });
  recordTracking({ kind: "lead", event: "generate_lead", source_page, conversion_type, transaction_id: transaction_id || "" });

  if (!gtagReady()) {
    return { event: "form_submit", source_page, conversion_type, sandbox: TRACKING_SANDBOX };
  }

  // GA4 — generate_lead (recommended event name for lead form submission)
  if (hasGA4()) {
    window.gtag("event", "generate_lead", {
      send_to: TRACKING.ga4Id,
      ...(value !== undefined ? { value, currency } : {}),
      ...(transaction_id ? { transaction_id } : {}),
      source_page,
      conversion_type,
    });
  }

  // Google Ads — single FCA Website Lead conversion (only fires if both
  // Conversion ID + Label are set). Format: "AW-CONVERSION_ID/CONVERSION_LABEL".
  // No user_data / Enhanced Conversions — identity is never sent to Google.
  if (hasGoogleAdsConversion()) {
    window.gtag("event", "conversion", {
      send_to: `${TRACKING.googleAdsId}/${TRACKING.googleAdsConversionLabel}`,
      ...(value !== undefined ? { value, currency } : {}),
      ...(transaction_id ? { transaction_id } : {}),
    });
  }

  return { event: "form_submit", source_page, conversion_type };
}

// ─── Server-side click-conversion safety-net ────────────────────────────────
// Phone/email clicks and PDF downloads fire a Google Ads conversion but do NOT
// submit a form, so they used to be invisible in /admin/leads. We now POST a
// lightweight record to /api/conversion-event so every conversion the website
// records is also retrievable from the admin dashboard — never just inbox-only.
//
// Fire-and-forget: never block the user interaction. Failure is swallowed.
const SERVER_SIDE_LOGGABLE = new Set([
  "phone_click",
  "email_click",
  "coordinator_pack_download",
  "sample_report_request",
  "coordinator_enquiry",
  "meet_assessors_book_click",
]);
function logConversionServerSide(eventName, params = {}) {
  try {
    if (typeof window === "undefined" || !SERVER_SIDE_LOGGABLE.has(eventName)) return;
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    if (!apiBase) return;
    const body = {
      event: eventName,
      source_page: params.source_page || params.sourcePage || window.location.pathname,
      role: params.role || "",
      asset: params.asset || "",
      extra: { conversion_type: params.conversion_type || "", duplicate: !!params.duplicate, ...params.extra },
      referrer: typeof document !== "undefined" ? document.referrer : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    };
    // keepalive lets the request complete even if the page is navigating away
    // (e.g. tap-to-call leaving the page).
    fetch(`${apiBase}/api/conversion-event`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "omit",
    })
      .then((res) => {
        // NON-2xx responses used to be silently swallowed — that's how
        // the July-2026 tracking outage went unnoticed for a week. Now
        // any failure is visible in DevTools console + persisted via
        // the /api/tracking-failure beacon so the admin dashboard can
        // surface it.
        if (!res || !res.ok) {
          const statusCode = res ? res.status : 0;
          try { console.error(`[tracking] ${eventName} POST failed status=${statusCode}`); } catch (_) {}
          reportTrackingFailure({
            kind: eventName,
            endpoint_path: "/api/conversion-event",
            status_code: statusCode,
            error_reason: `HTTP ${statusCode}`,
            source_page: body.source_page,
          });
        }
      })
      .catch((err) => {
        // Network / CORS / DNS failure.
        try { console.error(`[tracking] ${eventName} network failure`, err); } catch (_) {}
        reportTrackingFailure({
          kind: eventName,
          endpoint_path: "/api/conversion-event",
          status_code: null,
          error_reason: (err && (err.name + ": " + (err.message || ""))) || "network_error",
          source_page: body.source_page,
        });
      });
  } catch (_) { /* never block UI */ }
}

// Beacon reporter — sends a small allow-listed payload to
// /api/tracking-failure. Also fire-and-forget (we don't cascade a
// failure of the failure-reporter), but the failure of the FIRST call
// is now durably recorded server-side, so an outage cannot go silent.
function reportTrackingFailure(payload) {
  try {
    const apiBase = process.env.REACT_APP_BACKEND_URL || "";
    if (!apiBase) return;
    fetch(`${apiBase}/api/tracking-failure`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
      keepalive: true,
      credentials: "omit",
    }).catch(() => { /* last-line — swallow to avoid infinite loop */ });
  } catch (_) { /* never block UI */ }
}

export const trackPhoneClick = (params = {}) => {
  logConversionServerSide("phone_click", params);
  return trackConversion("phone_click", params);
};
export const trackEmailClick = (params = {}) => {
  logConversionServerSide("email_click", params);
  return trackConversion("email_click", params);
};

// ─── Support Coordinator funnel — fire on the dedicated coordinator landing page ───
//
// trackSampleReportRequest()    — user opens / submits the "Request a Sample Report" lead form
// trackCoordinatorEnquiry()     — user clicks "Request a Free Call" or general coord enquiry CTA
// trackCoordinatorPackDownload() — user clicks the "Download the PDF Pack" button
//
// Each is a MICRO-engagement — analytics-only. They fire GA4 events + dataLayer
// pushes + a server-side row in click_conversions for the admin dashboard, but
// deliberately do NOT fire the Google Ads FCA Website Lead primary conversion
// (see trackConversion() comment above).
export const trackSampleReportRequest    = (params = {}) => { logConversionServerSide("sample_report_request", { conversion_type: "sample_report", ...params }); return trackConversion("sample_report_request",    { conversion_type: "sample_report", ...params }); };
export const trackCoordinatorEnquiry     = (params = {}) => { logConversionServerSide("coordinator_enquiry",   { conversion_type: "coordinator_enquiry", ...params }); return trackConversion("coordinator_enquiry",      { conversion_type: "coordinator_enquiry", ...params }); };
export const trackCoordinatorPackDownload = (params = {}) => { logConversionServerSide("coordinator_pack_download", { conversion_type: "coordinator_pack_download", ...params }); return trackConversion("coordinator_pack_download", { conversion_type: "coordinator_pack_download", ...params }); };
